<template>
  <div class="min-h-screen flex flex-col">
    <NavBar :title="''" />

    <section class="flex-1 bg-gradient-to-br from-blue-500 to-slate-800 pt-16 pb-24">
      <div class="max-w-3xl mx-auto px-4 py-4">
        <!-- User Info Card -->
        <div class="bg-white/95 backdrop-blur-lg rounded-2xl p-4 md:p-6 mb-4 shadow-xl">
          <div class="flex flex-col md:flex-row items-center gap-4 mb-4 pb-4 border-b border-gray-200">
            <div class="text-5xl md:text-6xl text-blue-500">
              <i class="fas fa-user-circle"></i>
            </div>
            <div class="text-center md:text-left">
              <h2 class="text-lg md:text-xl font-bold text-gray-800 mb-1">
                Welcome, {{ storedUser.firstname }} {{ storedUser.lastname }}
              </h2>
              <span class="inline-flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full text-sm text-gray-600 font-medium">
                <i class="fas fa-truck"></i> {{ truckNumber }}
              </span>
            </div>
          </div>

          <!-- Stats Grid -->
          <div class="grid grid-cols-2 gap-3">
            <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div class="w-12 h-12 bg-green-100 text-green-600 rounded-xl flex items-center justify-center text-xl">
                <i class="fas fa-check-circle"></i>
              </div>
              <div>
                <span class="block text-2xl font-bold text-gray-800 leading-tight">{{ completedStops }}</span>
                <span class="text-xs text-gray-500 uppercase tracking-wide">Completed</span>
              </div>
            </div>
            <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div class="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center text-xl">
                <i class="fas fa-tasks"></i>
              </div>
              <div>
                <span class="block text-2xl font-bold text-gray-800 leading-tight">{{ remainingStops }}</span>
                <span class="text-xs text-gray-500 uppercase tracking-wide">Remaining</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loadingRoutes" class="bg-white/95 backdrop-blur-lg rounded-xl p-8">
          <div class="flex flex-col items-center gap-4">
            <div class="w-10 h-10 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            <p class="text-gray-500">Fetching route plan...</p>
          </div>
        </div>

        <!-- Route Content -->
        <div v-else>
          <div v-if="currentRoute">
            <!-- Route Info Card -->
            <div class="bg-white/95 backdrop-blur-lg rounded-2xl p-4 md:p-6 mb-4 shadow-xl">
              <!-- Route Header -->
              <div class="flex flex-wrap justify-between items-center gap-2 mb-3">
                <h3 class="text-lg font-semibold text-gray-800">
                  <i class="fas fa-route mr-2 text-blue-500"></i>Today's Route
                </h3>
                <div class="flex items-center gap-2">
                  <span v-if="currentRoute.nfRouteType" class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    {{ currentRoute.nfRouteType }}
                  </span>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase"
                    :class="{
                      'bg-gray-100 text-gray-600': currentRoute.status === 'PLANNED',
                      'bg-blue-100 text-blue-600': currentRoute.status === 'IN_PROGRESS',
                      'bg-green-100 text-green-600': currentRoute.status === 'COMPLETED'
                    }"
                  >
                    {{ routeStatusLabel }}
                  </span>
                </div>
              </div>

              <!-- Route Details -->
              <div class="space-y-2 mb-4 text-sm text-gray-600">
                <div v-if="currentRoute.driverName" class="flex items-center gap-2">
                  <i class="fas fa-user-circle text-gray-400 w-4 text-center"></i>
                  <span>{{ currentRoute.driverName }}</span>
                </div>
                <div v-if="currentRoute.scheduledDate" class="flex items-center gap-2">
                  <i class="fas fa-calendar-alt text-gray-400 w-4 text-center"></i>
                  <span>{{ formatDate(currentRoute.scheduledDate) }}</span>
                </div>
                <div v-if="currentRoute.origin && currentRoute.origin.name" class="flex items-center gap-2">
                  <i class="fas fa-warehouse text-gray-400 w-4 text-center"></i>
                  <span>{{ currentRoute.origin.name }}</span>
                  <span v-if="currentRoute.finalDepot && currentRoute.finalDepot.name !== currentRoute.origin.name" class="text-gray-400">
                    <i class="fas fa-arrow-right mx-1 text-xs"></i>{{ currentRoute.finalDepot.name }}
                  </span>
                </div>
              </div>

              <!-- Route Summary Stats -->
              <div v-if="currentRoute.summary" class="grid grid-cols-4 gap-2 mb-4">
                <div class="text-center p-2 bg-gray-50 rounded-lg">
                  <span class="block text-sm font-bold text-gray-800">{{ currentRoute.stops.length }}</span>
                  <span class="text-xs text-gray-500">Stops</span>
                </div>
                <div class="text-center p-2 bg-gray-50 rounded-lg">
                  <span class="block text-sm font-bold text-gray-800">{{ currentRoute.summary.totalDurationFormatted }}</span>
                  <span class="text-xs text-gray-500">Duration</span>
                </div>
                <div class="text-center p-2 bg-gray-50 rounded-lg">
                  <span class="block text-sm font-bold text-gray-800">{{ formatDistance(currentRoute.summary.totalDistanceMeters) }}</span>
                  <span class="text-xs text-gray-500">Distance</span>
                </div>
                <div class="text-center p-2 bg-gray-50 rounded-lg">
                  <span class="block text-sm font-bold text-gray-800">{{ formatTime(currentRoute.summary.estimatedEndTime) }}</span>
                  <span class="text-xs text-gray-500">End ETA</span>
                </div>
              </div>

              <!-- Start Route Button -->
              <button
                v-if="currentRoute.status === 'PLANNED'"
                :disabled="loadingAction"
                class="w-full h-13 flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl shadow-lg shadow-green-500/30 transition-all active:scale-[0.98] disabled:opacity-50"
                @click="startRoute"
              >
                <span v-if="loadingAction" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                <template v-else>
                  <i class="fas fa-play"></i>
                  <span>Start Route</span>
                </template>
              </button>

              <!-- Route Completed Message -->
              <div v-if="currentRoute.status === 'COMPLETED'" class="flex items-center justify-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 font-medium">
                <i class="fas fa-flag-checkered"></i>
                <span>Route completed</span>
              </div>
            </div>

            <!-- Current Stop Card -->
            <div v-if="currentRoute.status === 'IN_PROGRESS' && currentStop" class="mb-4">
              <h3 class="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <i class="fas fa-map-marker-alt"></i>
                Stop {{ currentStop.sequence }} of {{ currentRoute.stops.length }}
              </h3>

              <div class="bg-white/95 backdrop-blur-lg rounded-xl p-4 shadow-xl">
                <!-- Stop Info -->
                <div class="mb-4">
                  <div class="flex items-start justify-between gap-2 mb-1">
                    <h4 class="text-lg font-semibold text-gray-800">{{ currentStop.supplierCustomerName }}</h4>
                    <!-- Navigate Button -->
                    <a
                      v-if="currentStop.coordinates"
                      :href="'https://www.google.com/maps/dir/?api=1&destination=' + currentStop.coordinates.lat + ',' + currentStop.coordinates.lng"
                      target="_blank"
                      class="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors"
                      title="Navigate"
                    >
                      <i class="fas fa-directions"></i>
                    </a>
                  </div>
                  <p v-if="currentStop.locationName" class="text-sm text-gray-500 mb-3">
                    <i class="fas fa-map-pin mr-1"></i>{{ currentStop.locationName }}
                  </p>

                  <!-- Stop Detail Grid -->
                  <div class="grid grid-cols-2 gap-2 text-sm">
                    <div v-if="currentStop.estimatedArrival" class="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <i class="fas fa-sign-in-alt text-blue-500 w-4 text-center"></i>
                      <div>
                        <span class="block text-xs text-gray-400">Arrive</span>
                        <span class="font-semibold text-gray-700">{{ formatTime(currentStop.estimatedArrival) }}</span>
                      </div>
                    </div>
                    <div v-if="currentStop.estimatedDeparture" class="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <i class="fas fa-sign-out-alt text-blue-500 w-4 text-center"></i>
                      <div>
                        <span class="block text-xs text-gray-400">Depart</span>
                        <span class="font-semibold text-gray-700">{{ formatTime(currentStop.estimatedDeparture) }}</span>
                      </div>
                    </div>
                    <div v-if="currentStop.legFromPrevious" class="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <i class="fas fa-road text-blue-500 w-4 text-center"></i>
                      <div>
                        <span class="block text-xs text-gray-400">From prev</span>
                        <span class="font-semibold text-gray-700">{{ currentStop.legFromPrevious.durationFormatted }} &middot; {{ formatDistance(currentStop.legFromPrevious.distanceMeters) }}</span>
                      </div>
                    </div>
                    <div v-if="currentStop.serviceDuration" class="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <i class="fas fa-hourglass-half text-blue-500 w-4 text-center"></i>
                      <div>
                        <span class="block text-xs text-gray-400">Service</span>
                        <span class="font-semibold text-gray-700">{{ currentStop.serviceDuration }} min</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Stop Status Badge -->
                <div v-if="currentStop.status" class="mb-4">
                  <span
                    class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold uppercase"
                    :class="stopStatusClass(currentStop.status)"
                  >
                    <i :class="stopStatusIcon(currentStop.status)"></i>
                    {{ currentStop.status }}
                  </span>
                </div>

                <!-- Action Buttons -->
                <div class="flex flex-col md:flex-row gap-3">
                  <!-- Pending: Arrived + Skip -->
                  <template v-if="!currentStop.status">
                    <button
                      :disabled="loadingAction"
                      class="flex-1 h-13 flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl shadow-lg shadow-green-500/30 transition-all active:scale-[0.98] disabled:opacity-50"
                      @click="markArrived"
                    >
                      <span v-if="loadingAction" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      <template v-else>
                        <i class="fas fa-map-marker-alt"></i>
                        <span>Mark Arrived</span>
                      </template>
                    </button>
                    <button
                      :disabled="loadingAction"
                      class="flex-1 h-13 flex items-center justify-center gap-2 border-2 border-amber-500 text-amber-600 font-semibold rounded-xl transition-all hover:bg-amber-50 active:scale-[0.98] disabled:opacity-50"
                      @click="skipStop"
                    >
                      <i class="fas fa-forward"></i>
                      <span>Skip for Later</span>
                    </button>
                  </template>

                  <!-- Visited: Collect + Fail -->
                  <template v-if="currentStop.status === 'VISITED'">
                    <button
                      class="flex-1 h-13 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98]"
                      @click="goToCollect"
                    >
                      <i class="fas fa-qrcode"></i>
                      <span>Scan & Collect</span>
                    </button>
                    <button
                      class="flex-1 h-13 flex items-center justify-center gap-2 border-2 border-red-500 text-red-500 font-semibold rounded-xl transition-all hover:bg-red-50 active:scale-[0.98]"
                      @click="showFailDialog = true"
                    >
                      <i class="fas fa-times-circle"></i>
                      <span>Fail Collection</span>
                    </button>
                    <button
                      class="flex-1 h-13 flex items-center justify-center gap-2 border-2 border-indigo-500 text-indigo-600 font-semibold rounded-xl transition-all hover:bg-indigo-50 active:scale-[0.98]"
                      @click="openDeliverDialog"
                    >
                      <i class="fas fa-box"></i>
                      <span>Deliver Container</span>
                    </button>
                  </template>

                  <!-- Completed / Failed: Next or End -->
                  <template v-if="currentStop.status === 'COMPLETED' || currentStop.status === 'FAILED'">
                    <template v-if="isLastStop">
                      <button
                        :disabled="loadingAction"
                        class="flex-1 h-13 flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl shadow-lg shadow-red-500/30 transition-all active:scale-[0.98] disabled:opacity-50"
                        @click="completeRoute"
                      >
                        <span v-if="loadingAction" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        <template v-else>
                          <span>Complete Route</span>
                          <i class="fas fa-flag-checkered"></i>
                        </template>
                      </button>
                    </template>
                    <button
                      v-else
                      :disabled="loadingAction"
                      class="flex-1 h-13 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/30 transition-all active:scale-[0.98] disabled:opacity-50"
                      @click="departStop"
                    >
                      <span v-if="loadingAction" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      <template v-else>
                        <span>Next Stop</span>
                        <i class="fas fa-chevron-right"></i>
                      </template>
                    </button>
                  </template>
                </div>
              </div>
            </div>

            <!-- Upcoming Stops -->
            <div v-if="currentRoute.status === 'IN_PROGRESS' && upcomingStops.length > 0" class="mb-4">
              <h3 class="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <i class="fas fa-list"></i> Upcoming ({{ upcomingStops.length }})
              </h3>
              <div class="bg-white/95 backdrop-blur-lg rounded-xl p-4 shadow-xl">
                <ul class="divide-y divide-gray-100">
                  <li
                    v-for="stop in upcomingStops"
                    :key="stop.sequence"
                    class="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                  >
                    <span class="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center text-sm font-semibold text-gray-600 flex-shrink-0">
                      {{ stop.sequence }}
                    </span>
                    <div class="flex-1 min-w-0">
                      <span class="block text-gray-700 font-medium truncate">{{ stop.supplierCustomerName }}</span>
                      <span v-if="stop.locationName" class="block text-xs text-gray-400 truncate">{{ stop.locationName }}</span>
                    </div>
                    <div class="flex flex-col items-end gap-0.5 flex-shrink-0">
                      <span v-if="stop.estimatedArrival" class="text-xs font-medium text-gray-600">
                        {{ formatTime(stop.estimatedArrival) }}
                      </span>
                      <span v-if="stop.legFromPrevious" class="text-xs text-gray-400 whitespace-nowrap">
                        {{ stop.legFromPrevious.durationFormatted }} &middot; {{ formatDistance(stop.legFromPrevious.distanceMeters) }}
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <!-- All Stops Done — Complete Route -->
            <div
              v-if="currentRoute.status === 'IN_PROGRESS' && !currentStop && upcomingStops.length === 0"
              class="mb-4 space-y-4"
            >
              <div class="bg-green-50 border border-green-200 rounded-xl p-6 flex flex-col items-center gap-2 text-green-700">
                <i class="fas fa-check-circle text-4xl"></i>
                <p class="font-medium">All stops complete!</p>
                <p class="text-sm text-green-600">Head to the Deliver tab to submit UCO at the warehouse</p>
              </div>

              <!-- Complete Route -->
              <button
                :disabled="loadingAction"
                class="w-full h-14 flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl shadow-lg shadow-red-500/30 transition-all active:scale-[0.98] disabled:opacity-50"
                @click="completeRoute"
              >
                <span v-if="loadingAction" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                <template v-else>
                  <span>Complete Route</span>
                  <i class="fas fa-flag-checkered"></i>
                </template>
              </button>
            </div>
          </div>

          <!-- No Routes -->
          <div v-else class="bg-amber-50 border border-amber-200 rounded-xl p-8 flex flex-col items-center gap-3 text-amber-700">
            <i class="fas fa-calendar-times text-4xl"></i>
            <p class="font-medium">No planned route for today</p>
          </div>
        </div>

        <!-- Standalone Collect Button (when no route or route completed) -->
        <div v-if="!loadingRoutes && (!currentRoute || currentRoute.status === 'COMPLETED')" class="mt-4">
          <button
            class="w-full h-16 flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-xl rounded-xl shadow-lg shadow-blue-500/30 transition-all hover:from-blue-600 hover:to-blue-700 active:scale-[0.98]"
            @click="navigate('collectHome')"
          >
            <i class="fas fa-qrcode"></i>
            <span>Standalone Collection</span>
          </button>
        </div>
      </div>
    </section>

    <!-- Fail Collection Dialog -->
    <transition name="fade">
      <div v-if="showFailDialog" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div class="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" @click.stop>
          <div class="text-center mb-4">
            <div class="inline-flex items-center justify-center w-14 h-14 bg-red-100 text-red-500 rounded-full text-3xl mb-3">
              <i class="fas fa-times-circle"></i>
            </div>
            <h3 class="text-lg font-bold text-gray-800">Fail Collection</h3>
            <p class="text-sm text-gray-500 mt-1">{{ currentStop?.supplierCustomerName }}</p>
          </div>

          <div class="space-y-3 mb-5">
            <label class="text-sm font-semibold text-gray-700">Reason</label>
            <select
              v-model="failReason"
              class="w-full h-12 px-3 text-base border-2 border-gray-200 rounded-lg focus:border-red-500 focus:ring-4 focus:ring-red-500/20 outline-none"
            >
              <option value="" disabled>Select a reason</option>
              <option>Gate locked</option>
              <option>No one available</option>
              <option>No UCO available</option>
              <option>Wrong location</option>
              <option>Refused collection</option>
              <option>Other</option>
            </select>
            <input
              v-if="failReason === 'Other'"
              v-model="customFailReason"
              type="text"
              placeholder="Enter reason..."
              class="w-full h-12 px-3 text-base border-2 border-gray-200 rounded-lg focus:border-red-500 focus:ring-4 focus:ring-red-500/20 outline-none"
            />
          </div>

          <div class="flex gap-3">
            <button
              class="flex-1 h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
              @click="showFailDialog = false"
            >
              Cancel
            </button>
            <button
              :disabled="!failReasonText || loadingAction"
              class="flex-1 h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
              @click="failCollection"
            >
              <span v-if="loadingAction" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              <span v-else>Confirm</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Deliver Container Dialog -->
    <transition name="fade">
      <div v-if="showDeliverDialog" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div class="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" @click.stop>
          <div class="text-center mb-4">
            <div class="inline-flex items-center justify-center w-14 h-14 bg-indigo-100 text-indigo-500 rounded-full text-3xl mb-3">
              <i class="fas fa-box"></i>
            </div>
            <h3 class="text-lg font-bold text-gray-800">Deliver Container</h3>
            <p class="text-sm text-gray-500 mt-1">{{ currentStop?.supplierCustomerName }}</p>
          </div>

          <div class="space-y-4 mb-5">
            <div>
              <label class="text-sm font-semibold text-gray-700">Containers delivered</label>
              <div class="mt-1 space-y-2">
                <div v-for="(row, i) in containerRows" :key="i" class="flex items-center gap-2">
                  <select
                    v-model="row.containerTypeId"
                    class="flex-1 min-w-0 h-12 px-3 text-base border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 outline-none"
                  >
                    <option value="" disabled>{{ loadingContainerTypes ? 'Loading…' : 'Select a container' }}</option>
                    <option v-for="c in containerTypes" :key="c.id" :value="c.id">{{ c.containerType }}</option>
                  </select>
                  <input
                    v-model.number="row.quantity"
                    type="number"
                    min="1"
                    inputmode="numeric"
                    class="w-16 h-12 px-2 text-base text-center border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 outline-none"
                  />
                  <button
                    type="button"
                    class="w-9 h-12 flex items-center justify-center text-gray-400 hover:text-red-500 disabled:opacity-30"
                    :disabled="containerRows.length === 1"
                    @click="removeContainerRow(i)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <button
                type="button"
                class="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                @click="addContainerRow"
              >
                <i class="fas fa-plus"></i> Add container
              </button>
            </div>

            <div>
              <label class="text-sm font-semibold text-gray-700">Photo proof</label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                class="mt-1 w-full text-sm"
                @change="onPhotoSelected"
              />
              <img v-if="containerPhoto" :src="containerPhoto" class="mt-2 w-full h-40 object-cover rounded-lg border border-gray-200" />
            </div>
          </div>

          <div class="flex gap-3">
            <button
              class="flex-1 h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
              @click="showDeliverDialog = false"
            >
              Cancel
            </button>
            <button
              :disabled="!canDeliver || loadingAction"
              class="flex-1 h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
              @click="deliverContainer"
            >
              <span v-if="loadingAction" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              <span v-else>Confirm Delivery</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import NavBar from "@/components/NavBar.vue";
import { useStore } from "vuex";
import { useDriverTracking } from "@/composables/useDriverTracking";

import { authHeaders, forceReLogin } from "@/composables/apiAuth";

const fetchFromAPI = async (endpoint, payload) => {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (response.status === 401) {
    forceReLogin();
  }
  return response.json();
};

export default {
  name: "HomeView",
  components: {
    NavBar,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const userString = sessionStorage.getItem("user");
    const truckNumber = sessionStorage.getItem("truckNumber");
    const user = JSON.parse(userString);
    const siteId = user.siteid;

    const loadingRoutes = ref(true);
    const loadingAction = ref(false);
    const currentRoute = ref(null);

    const showFailDialog = ref(false);
    const failReason = ref("");
    const customFailReason = ref("");
    const showDeliverDialog = ref(false);
    const containerTypes = ref([]);
    const containerRows = ref([{ containerTypeId: "", quantity: 1 }]);
    const containerPhoto = ref(""); // base64 Data URL
    const loadingContainerTypes = ref(false);
    let storedUser = null;
    if (userString) {
      try {
        storedUser = JSON.parse(userString);
      } catch (error) {
        console.error("Error parsing user from session storage:", error);
      }
    }

    const { startTracking, stopTracking } = useDriverTracking();

    // Start watchPosition pings while the active route is IN_PROGRESS; stop
    // otherwise. The composable handles its own teardown if the server rejects
    // pings (e.g. driver hit "Mark Route Complete" elsewhere).
    watch(
      () => [currentRoute.value?.status, currentRoute.value?.route_id],
      ([status, routeId]) => {
        if (status === "IN_PROGRESS" && routeId) {
          startTracking(routeId);
        } else {
          stopTracking();
        }
      },
      { immediate: true }
    );

    // --- Computed ---

    const currentStop = computed(() => {
      if (!currentRoute.value || !currentRoute.value.stops) return null;
      return currentRoute.value.stops.find(
        (s) => s.status !== "DEPARTED" && s.status !== "SKIPPED"
      ) || null;
    });

    const upcomingStops = computed(() => {
      if (!currentRoute.value || !currentRoute.value.stops || !currentStop.value) return [];
      return currentRoute.value.stops.filter(
        (s) => s.sequence > currentStop.value.sequence
      );
    });

    const isLastStop = computed(() => {
      if (!currentStop.value || !currentRoute.value) return false;
      return currentStop.value.sequence === currentRoute.value.stops.length;
    });

    const completedStops = computed(() => {
      if (!currentRoute.value || !currentRoute.value.stops) return 0;
      return currentRoute.value.stops.filter(
        (s) => s.status === "COMPLETED" || s.status === "DEPARTED"
      ).length;
    });

    const remainingStops = computed(() => {
      if (!currentRoute.value || !currentRoute.value.stops) return 0;
      return currentRoute.value.stops.filter(
        (s) => !s.status || s.status === "VISITED"
      ).length;
    });

    const routeStatusLabel = computed(() => {
      const labels = { PLANNED: "Planned", IN_PROGRESS: "In Progress", COMPLETED: "Completed" };
      return labels[currentRoute.value?.status] || currentRoute.value?.status;
    });

    const failReasonText = computed(() => {
      if (failReason.value === "Other") return customFailReason.value.trim();
      return failReason.value;
    });

    // Rows with a chosen type + valid quantity; used to build the payload + gate Confirm.
    const validContainerRows = computed(() =>
      containerRows.value.filter((r) => r.containerTypeId && (r.quantity || 0) >= 1)
    );
    const canDeliver = computed(() => validContainerRows.value.length > 0 && !!containerPhoto.value);

    const addContainerRow = () => { containerRows.value.push({ containerTypeId: "", quantity: 1 }); };
    const removeContainerRow = (i) => {
      containerRows.value.splice(i, 1);
      if (containerRows.value.length === 0) containerRows.value.push({ containerTypeId: "", quantity: 1 });
    };

    // --- API Calls ---

    const getRoutes = async () => {
      try {
        const data = await fetchFromAPI(
          store.state.api + "checkForPlannedRoutes",
          { truckNumber, siteId }
        );

        if (data[2] === 200 && data[1].routes && data[1].routes.length > 0) {
          // Only surface routes the office has assigned to the currently
          // logged-in driver. Two drivers sharing a truck across shifts can
          // each see only their own route. Routes without driverId (legacy
          // records pre-dating the OMS driverId fix) are intentionally
          // excluded — they predate driver assignment and shouldn't be
          // claimed implicitly.
          const myRoutes = data[1].routes.filter(
            (r) => r.driverId && r.driverId === user.driverId
          );

          if (myRoutes.length === 0) {
            currentRoute.value = null;
          } else {
            // Pick the first active route (IN_PROGRESS first, then PLANNED),
            // fall back to the first route assigned to this driver.
            currentRoute.value =
              myRoutes.find((r) => r.status === "IN_PROGRESS") ||
              myRoutes.find((r) => r.status === "PLANNED") ||
              myRoutes[0];
          }
        } else {
          currentRoute.value = null;
        }
      } catch (err) {
        console.error("Error fetching routes:", err);
        currentRoute.value = null;
      } finally {
        loadingRoutes.value = false;
      }
    };

    const getCurrentLocation = () => {
      return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            (err) => reject(err)
          );
        } else {
          reject(new Error("Geolocation not supported"));
        }
      });
    };

    const getLocationOrDefault = async () => {
      try {
        return await getCurrentLocation();
      } catch {
        console.warn("Could not get location, using default");
        return { lat: 0, lng: 0 };
      }
    };

    // --- Route Actions ---

    const startRoute = async () => {
      if (!currentRoute.value) return;
      loadingAction.value = true;
      try {
        const loc = await getLocationOrDefault();
        await fetchFromAPI(store.state.api + "setRouteStatus", {
          routeId: currentRoute.value.route_id,
          addressId: "",
          status: "IN_PROGRESS",
          lat: loc.lat,
          lng: loc.lng,
        });
        await getRoutes();
      } catch (err) {
        console.error("Error starting route:", err);
      } finally {
        loadingAction.value = false;
      }
    };

    const completeRoute = async () => {
      if (!currentRoute.value) return;
      loadingAction.value = true;
      try {
        const loc = await getLocationOrDefault();

        // Depart current stop first if needed
        if (currentStop.value && (currentStop.value.status === "COMPLETED" || currentStop.value.status === "FAILED")) {
          await fetchFromAPI(store.state.api + "setAddressStatus", {
            routeId: currentRoute.value.route_id,
            addressId: currentStop.value.collectionRequestId,
            status: "DEPARTED",
            lat: loc.lat,
            lng: loc.lng,
          });
        }

        await fetchFromAPI(store.state.api + "setRouteStatus", {
          routeId: currentRoute.value.route_id,
          addressId: "",
          status: "COMPLETED",
          lat: loc.lat,
          lng: loc.lng,
        });
        await getRoutes();
      } catch (err) {
        console.error("Error completing route:", err);
      } finally {
        loadingAction.value = false;
      }
    };

    // --- Stop Actions ---

    const markArrived = async () => {
      if (!currentStop.value || !currentRoute.value) return;
      loadingAction.value = true;
      try {
        const loc = await getLocationOrDefault();
        await fetchFromAPI(store.state.api + "setAddressStatus", {
          routeId: currentRoute.value.route_id,
          addressId: currentStop.value.collectionRequestId,
          status: "VISITED",
          lat: loc.lat,
          lng: loc.lng,
        });
        await getRoutes();
      } catch (err) {
        console.error("Error marking arrived:", err);
      } finally {
        loadingAction.value = false;
      }
    };

    const skipStop = async () => {
      if (!currentStop.value || !currentRoute.value) return;
      loadingAction.value = true;
      try {
        // Move the stop to the end of the route so the driver can revisit it
        await fetchFromAPI(store.state.api + "deferStop", {
          routeId: currentRoute.value.route_id,
          addressId: currentStop.value.collectionRequestId,
        });
        await getRoutes();
      } catch (err) {
        console.error("Error skipping stop:", err);
      } finally {
        loadingAction.value = false;
      }
    };

    const departStop = async () => {
      if (!currentStop.value || !currentRoute.value) return;
      loadingAction.value = true;
      try {
        const loc = await getLocationOrDefault();
        await fetchFromAPI(store.state.api + "setAddressStatus", {
          routeId: currentRoute.value.route_id,
          addressId: currentStop.value.collectionRequestId,
          status: "DEPARTED",
          lat: loc.lat,
          lng: loc.lng,
        });
        await getRoutes();
      } catch (err) {
        console.error("Error departing stop:", err);
      } finally {
        loadingAction.value = false;
      }
    };

    const goToCollect = () => {
      if (!currentStop.value || !currentRoute.value) return;
      store.commit("SET_ROUTE_CONTEXT", {
        routeId: currentRoute.value.route_id,
        stopSequence: currentStop.value.sequence,
        collectionRequestId: currentStop.value.collectionRequestId,
      });
      router.push({ name: "collectHome" });
    };

    const failCollection = async () => {
      if (!currentStop.value || !currentRoute.value || !failReasonText.value) return;
      loadingAction.value = true;
      try {
        const loc = await getLocationOrDefault();
        const stopCrId = currentStop.value.collectionRequestId;

        // Fail the collection
        await fetchFromAPI(store.state.api + "failCollection", {
          orderId: stopCrId,
          reason: failReasonText.value,
          routeId: currentRoute.value.route_id,
          addressId: stopCrId,
        });

        // Auto-depart so the UI advances to the next stop
        await fetchFromAPI(store.state.api + "setAddressStatus", {
          routeId: currentRoute.value.route_id,
          addressId: stopCrId,
          status: "DEPARTED",
          lat: loc.lat,
          lng: loc.lng,
        });

        showFailDialog.value = false;
        failReason.value = "";
        customFailReason.value = "";
        await getRoutes();
      } catch (err) {
        console.error("Error failing collection:", err);
      } finally {
        loadingAction.value = false;
      }
    };

    const openDeliverDialog = async () => {
      // Clear any prior selection/photo so a cancelled-then-reopened dialog
      // (possibly on a different stop) never carries a stale photo/type.
      containerRows.value = [{ containerTypeId: "", quantity: 1 }];
      containerPhoto.value = "";
      showDeliverDialog.value = true;
      if (containerTypes.value.length === 0) {
        loadingContainerTypes.value = true;
        try {
          const data = await fetchFromAPI(store.state.api + "getContainerTypes", {});
          if (data[2] === 200 && data[1].containerTypes) {
            containerTypes.value = data[1].containerTypes;
          }
        } catch (err) {
          console.error("Error fetching container types:", err);
        } finally {
          loadingContainerTypes.value = false;
        }
      }
    };

    const onPhotoSelected = (event) => {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => { containerPhoto.value = e.target.result; };
      reader.readAsDataURL(file);
    };

    const deliverContainer = async () => {
      if (!currentStop.value || !currentRoute.value) return;
      if (!validContainerRows.value.length || !containerPhoto.value) return;
      loadingAction.value = true;
      try {
        const loc = await getLocationOrDefault();
        const stopCrId = currentStop.value.collectionRequestId;
        const containers = validContainerRows.value.map((r) => {
          const t = containerTypes.value.find((c) => c.id === r.containerTypeId);
          return { containerTypeId: r.containerTypeId, containerType: t ? t.containerType : "", quantity: Number(r.quantity) || 1 };
        });

        const data = await fetchFromAPI(store.state.api + "deliverContainer", {
          orderId: stopCrId,
          containers,
          photo: containerPhoto.value,
          routeId: currentRoute.value.route_id,
          addressId: stopCrId,
          stopSequence: currentStop.value.sequence,
          deliveredBy: storedUser ? `${storedUser.firstname} ${storedUser.lastname}` : null,
          lat: loc.lat,
          lng: loc.lng,
        });

        // Abort the auto-depart if the backend rejected the delivery. A success
        // is the tuple envelope ["Complete", {...}, 200]; an HTTPException (400/
        // 404/409) serializes to a {"detail": ...} object, so anything that is
        // not a 200 tuple means the delivery did NOT happen — do not advance.
        if (!Array.isArray(data) || data[2] !== 200) {
          console.error("Deliver container failed:", data);
          return;
        }

        // Auto-depart so the UI advances to the next stop (mirrors fail flow).
        await fetchFromAPI(store.state.api + "setAddressStatus", {
          routeId: currentRoute.value.route_id,
          addressId: stopCrId,
          status: "DEPARTED",
          lat: loc.lat,
          lng: loc.lng,
        });

        showDeliverDialog.value = false;
        containerRows.value = [{ containerTypeId: "", quantity: 1 }];
        containerPhoto.value = "";
        await getRoutes();
      } catch (err) {
        console.error("Error delivering container:", err);
      } finally {
        loadingAction.value = false;
      }
    };

    // --- Helpers ---

    const formatTime = (isoString) => {
      if (!isoString) return "";
      const date = new Date(isoString);
      return date.toLocaleTimeString("en-AE", { hour: "2-digit", minute: "2-digit", hour12: true });
    };

    const formatDate = (isoString) => {
      if (!isoString) return "";
      const date = new Date(isoString);
      return date.toLocaleDateString("en-AE", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
        + " " + date.toLocaleTimeString("en-AE", { hour: "2-digit", minute: "2-digit", hour12: true });
    };

    const formatDistance = (meters) => {
      if (!meters) return "0 km";
      return (meters / 1000).toFixed(1) + " km";
    };

    const stopStatusClass = (status) => ({
      "bg-blue-100 text-blue-600": status === "VISITED",
      "bg-green-100 text-green-600": status === "COMPLETED",
      "bg-red-100 text-red-600": status === "FAILED",
      "bg-amber-100 text-amber-600": status === "SKIPPED",
      "bg-gray-100 text-gray-600": status === "DEPARTED",
      "bg-indigo-100 text-indigo-600": status === "CONTAINER_DELIVERED",
    });

    const stopStatusIcon = (status) => ({
      "fas fa-map-marker-alt": status === "VISITED",
      "fas fa-check-circle": status === "COMPLETED",
      "fas fa-times-circle": status === "FAILED",
      "fas fa-forward": status === "SKIPPED",
      "fas fa-truck": status === "DEPARTED",
      "fas fa-box": status === "CONTAINER_DELIVERED",
    }[status] || "fas fa-circle");

    const navigate = (routeName) => {
      router.push({ name: routeName });
    };

    // --- Init ---
    getRoutes();

    return {
      storedUser,
      truckNumber,
      loadingRoutes,
      loadingAction,
      currentRoute,
      currentStop,
      upcomingStops,
      isLastStop,
      completedStops,
      remainingStops,
      routeStatusLabel,
      showFailDialog,
      failReason,
      customFailReason,
      failReasonText,
      showDeliverDialog,
      containerTypes,
      containerRows,
      canDeliver,
      addContainerRow,
      removeContainerRow,
      containerPhoto,
      loadingContainerTypes,
      openDeliverDialog,
      onPhotoSelected,
      deliverContainer,
      startRoute,
      completeRoute,
      markArrived,
      skipStop,
      departStop,
      goToCollect,
      failCollection,
      formatTime,
      formatDate,
      formatDistance,
      stopStatusClass,
      stopStatusIcon,
      navigate,
    };
  },
};
</script>

<style scoped>
.h-13 {
  height: 3.25rem;
}

.border-3 {
  border-width: 3px;
}
</style>
